import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { CUSTOM_ELEMENTS_SCHEMA, NgModule } from '@angular/core';
import { HttpClientModule, HttpClient, HTTP_INTERCEPTORS } from '@angular/common/http';
import { ConfigModule } from '@ng-darwin/config';
import { SecurityModule } from '@ng-darwin/security';
import { LoggerModule } from '@ng-darwin/logger';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { environment } from '../environments/environment';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { HttpRequestInterceptor } from '../app/interceptors/httpRequest.interceptor';
import { AgEntitiesSantanderCustomerComponent } from './components/ag-entities-santandercustomer/ag-entities-santandercustomer.component';
import { AgEntityComponent } from './components/ag-entity/ag-entity.component';
import { AgErrorComponent } from './components/ag-error/ag-error.component';
import { AgMessagesComponent } from './components/ag-messages/ag-messages.component';
import { AgTermsComponent } from './components/ag-terms/ag-terms.component';
import { AgTermsConditionsComponent } from './components/ag-terms-conditions/ag-terms-conditions.component';
import { ProgressbarComponent } from './helpers/progressbar/progressbar.component';
import { SlideComponent } from './helpers/slide/slide.component';
import { SpinnerComponent } from './helpers/spinner/spinner.component';
import { FormsModule } from '@angular/forms';
import { ModalComponent } from './helpers/modal/modal.component';
import { AgEntitiesSoftCustomerComponent } from './components/ag-entities-softcustomer/ag-entities-softcustomer.component';
import { AgEntitiesRootComponent } from './components/ag-entities-root/ag-entities-root.component';
import { SanCheckboxComponent } from './helpers/san-checkbox/san-checkbox.component';
import { AgBannerContainerComponent } from './components/ag-banner-container/ag-banner-container.component';
import { AgModalContainerComponent } from './components/ag-modal-container/ag-modal-container.component';
import { AgAdviseBannerComponent } from './components/ag-advise-banner/ag-advise-banner.component';
import { AgEntitiesLoaderComponent } from './components/ag-entities-loader/ag-entities-loader.component';
import { AgEntitiesAdviseModalComponent } from './components/ag-entities-advise-modal/ag-entities-advise-modal.component';
import { AgEntitiesInfoModalComponent } from './components/ag-entities-info-modal/ag-entities-info-modal.component';
import { AgEntitiesSuccessComponent } from './components/ag-entities-success/ag-entities-success.component';
import { AgButtonSetComponent } from './components/ag-button-set/ag-button-set.component';
import { AgEntitiesProgressComponent } from './components/ag-entities-progress/ag-entities-progress.component';
import { MatDialogModule } from '@angular/material/dialog';
import { FlameStencilAngularModule } from '@santander/flame-ui-angular';

/**
 * @description createTranslateLoader
 * @param http HttpClient
 * @returns TranslateHttpLoader
 */
export function createTranslateLoader(http: HttpClient): TranslateHttpLoader {
  return new TranslateHttpLoader(http, '../assets/i18n/', '.json');
}

/**
 * @description AppModule
 * Designed to be the root module.
 *
 * imports:
 *  - BrowserModule. Required infrastructure for all Angular apps.
 *  - HttpClientModule. Configures the DI for HttpClient.
 *  - ConfigModule. Configures the DI for ConfigService..
 *  - SecurityModule. Configures the DI for SecurityService
 *  - LoggerModule. Configures the DI for LoggerService.
 *  - AppRoutingModule. Module with the configured routes
 *
 * declarations:
 *  - AppComponent. Root component in charge of being the app shell.
 *  - DwComponent. Root component for Darwin app.
 *
 * bootstrap:
 *   - AppComponent
 */
@NgModule({
  declarations: [
    AppComponent,
    AgEntitiesSantanderCustomerComponent,
    AgEntitiesSoftCustomerComponent,
    AgEntityComponent,
    AgErrorComponent,
    AgMessagesComponent,
    AgTermsComponent,
    AgTermsConditionsComponent,
    ProgressbarComponent,
    SlideComponent,
    SpinnerComponent,
    ModalComponent,
    AgEntitiesRootComponent,
    SanCheckboxComponent,
    AgBannerContainerComponent,
    AgModalContainerComponent,
    AgAdviseBannerComponent,
    AgEntitiesLoaderComponent,
    AgEntitiesAdviseModalComponent,
    AgEntitiesInfoModalComponent,
    AgEntitiesSuccessComponent,
    AgButtonSetComponent,
    AgEntitiesProgressComponent
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    FormsModule,
    MatDialogModule,
    HttpClientModule,
    FlameStencilAngularModule,
    ConfigModule.forRoot({
      technicalGrouping: 'f-ng-50075370-spa',
      logEvent: {
        level: environment.configLogLevel,
        // eslint-disable-next-line no-console
        handler(log): void { console.log('app.module.ts: Log event from Config module', log); }
      },
      errorEvent: {
        // eslint-disable-next-line no-console
        handler(err): void { console.log('app.module.ts: Error event from Config module', err); }
      },
      configLoadedEvent: {
        // eslint-disable-next-line no-console
        handler(config): void { console.log('app.module.ts: ConfigLoadedEvent from Config module', config); }
      }
    }),
    SecurityModule,
    LoggerModule,
    AppRoutingModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: createTranslateLoader,
        deps: [HttpClient]
      },
      isolate: false
    }),
  ],
  providers: [
    { provide: HTTP_INTERCEPTORS, useClass: HttpRequestInterceptor, multi: true }
  ],
  bootstrap: [AppComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class AppModule { }
